import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import {
  NextSeo,
  OrganizationJsonLd,
  SiteLinksSearchBoxJsonLd,
} from 'next-seo';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Container from '@components/global/Container';
import Layout from '@components/global/Layout';
import { getHomepage } from '@adapters/prismic/fetching/homepage';
import getAllPromoTickerData from '@adapters/prismic/fetching/promos/getAllPromoTickerData';
import getBannerStripGlobalData from '@adapters/prismic/fetching/banner-strip-global/getBannerStripGlobalData';
import { PromoTicker } from '@adapters/prismic/types/promoTicker';
import type { GetStaticPropsContext, GetStaticPropsResult } from 'next';
import { getAllStoresDataForSetMyStore } from '@adapters/prismic/fetching/stores';
import { MyStoreData } from '@adapters/prismic/fetching/stores/types';
import { PageType } from '@adapters/prismic/types/slices';
import { altLocale } from '@utils/locale';
import { localeAltPathAbs, localePathAbs } from '@utils/url';
import useObjectVisibility from '@hooks/useObjectVisibility';
import getPromoBarData from '@adapters/prismic/fetching/promos/getPromoBarData';
import { FEATURE_FLAG_PROMO_BAR_SERVER_SIDE } from '@config/feature-flag';

import RecommendationsLoader, {
  AlgonomyPlacement,
} from '@components/global/Recommendations/Loader';
import Skeleton from 'react-loading-skeleton';

import styles from './HomePage.module.scss';
import { getPreviewRef } from '@adapters/prismic/utils/getPreviewRef';

const HomepageSliceZone = dynamic(
  () => import('@components/cms/HomepageSliceZone')
);

interface HomePageProps {
  description: string;
  title: string;
  firstTwoSlices: any[];
  belowFoldSlices: any[];
  canonicalUrl: string;
  langAltUrl: string;
  promoMessages?: PromoTicker[] | [];
  xDefault: string;
  serverSideTranslation: any | null;
  stores: any | null;
}

interface MECStores {
  stores: MyStoreData[];
}

export async function getStaticPaths() {
  return {
    paths: [{ params: { locale: 'en' } }, { params: { locale: 'fr' } }],
    fallback: false,
  };
}

export async function getStaticProps({
  params,
  previewData,
  preview = false,
}: GetStaticPropsContext): Promise<GetStaticPropsResult<
  HomePageProps | MECStores
> | null> {
  const locale = params?.locale === 'fr' ? 'fr' : 'en';
  const defaultLocale = 'en';
  if (!locale) {
    throw new Error('No locale specified for page');
  }

  const previewRef = getPreviewRef(preview, previewData);

  const canonicalUrl = localePathAbs(locale, '') ?? '';
  const langAltUrl = localeAltPathAbs(locale, '') ?? null;
  const xDefault = locale === 'en' ? canonicalUrl : langAltUrl;

  const [
    homepageData,
    stores,
    promoMessages,
    bannerStripData,
    serverSideTranslation,
    promoBarData,
  ] = await Promise.allSettled([
    getHomepage(locale ?? defaultLocale, preview ? previewRef : undefined),
    getAllStoresDataForSetMyStore(locale),
    !FEATURE_FLAG_PROMO_BAR_SERVER_SIDE &&
      locale &&
      getAllPromoTickerData(locale),
    getBannerStripGlobalData(locale, preview ? previewRef : undefined),
    serverSideTranslations(locale, ['common', 'promotions', 'organization']),
    FEATURE_FLAG_PROMO_BAR_SERVER_SIDE && locale && getPromoBarData(locale),
  ]).then((results) =>
    results.map((promise) => {
      if (
        promise.status === 'rejected' ||
        typeof promise.value === 'undefined'
      ) {
        console.error('promise rejected or returning undefined', promise);
        return null;
      }
      return promise.value;
    })
  );

  const description = homepageData?.results[0]?.data?.meta_description ?? '404';
  const title = homepageData?.results[0]?.data?.meta_title ?? '404';
  const body = homepageData?.results[0]?.data?.body1 ?? [];

  const [firstSlice, secondSlice, ...belowFoldSlices] = body;
  const firstTwoSlices = [firstSlice, secondSlice];

  return {
    props: {
      description,
      title,
      firstTwoSlices,
      belowFoldSlices,
      stores,
      canonicalUrl,
      langAltUrl,
      xDefault,
      ...serverSideTranslation,
      promoMessages,
      bannerStripData,
      hideTopRibbonAndStoreSelector: true,
      promoBarData,
    },
  };
}

export default function Home({
  description,
  title,
  firstTwoSlices,
  belowFoldSlices,
  canonicalUrl,
  langAltUrl,
  xDefault,
}: HomePageProps) {
  const sliceZoneRef = useRef<any>(null);
  const sliceZoneRefValue = useObjectVisibility(sliceZoneRef, 0.0001);
  const [isSliceZoneRef, setIsSliceZoneRef] = useState(false);

  const { query } = useRouter();
  const locale = (query.locale as string) || 'en';
  const { t } = useTranslation('organization');

  const [recommendations, setRecommendations] = useState<AlgonomyPlacement[]>(
    []
  );

  useEffect(() => {
    if (!isSliceZoneRef) setIsSliceZoneRef(sliceZoneRefValue);
  }, [isSliceZoneRef, sliceZoneRefValue]);

  if (!firstTwoSlices) return null;

  const heroImage = firstTwoSlices.find(
    (slice) =>
      slice.slice_type === 'hero_image' || slice.slice_type === 'hero_banner'
  )?.primary;

  return (
    <>
      <NextSeo
        description={description}
        title={title}
        canonical={canonicalUrl}
        languageAlternates={[
          { hrefLang: altLocale(locale), href: langAltUrl },
          { hrefLang: String(locale), href: canonicalUrl },
          { hrefLang: 'x-default', href: xDefault },
        ]}
        openGraph={{
          title,
          description,
          locale,
          images: [
            {
              url: heroImage?.image?.url || heroImage?.desktop_image?.url,
              width:
                heroImage?.image?.dimensions?.width ||
                heroImage?.desktop_image?.dimensions?.width,
              height:
                heroImage?.image?.dimensions?.height ||
                heroImage?.desktop_image?.dimensions?.height,
              alt: heroImage?.image?.alt || heroImage?.desktop_image?.alt,
            },
          ],
        }}
      />

      <SiteLinksSearchBoxJsonLd
        url={'https://www.mec.ca/'}
        potentialActions={[]}
        name="Mountain Equipment Company"
        alternateName="MEC"
      />
      <OrganizationJsonLd
        type="Corporation"
        id="https://www.mec.ca/#corporation"
        logo="https://mec.ca/images/mec-logo.svg"
        legalName="MEC Mountain Equipment Company Ltd."
        name="MEC"
        url="https://www.mec.ca"
        foundingDate="1971"
        address={{
          streetAddress: t('organization.address.street'),
          addressLocality: t('organization.address.city'),
          addressRegion: t('organization.address.province'),
          postalCode: t('organization.address.postal_code'),
          addressCountry: t('organization.address.country'),
        }}
        contactPoint={[
          {
            telephone: t('organization.contact.customer_service_phone_number'),
            contactType: t('organization.contact.customer_service'),
            availableLanguage: [
              t('organization.contact.language.english'),
              t('organization.contact.language.french'),
            ],
          },
          {
            telephone: t(
              'organization.contact.customer_service_phone_number_toll_free'
            ),
            contactType: t('organization.contact.customer_service'),
            contactOption: 'TollFree',
            areaServed: 'CA',
            availableLanguage: [
              t('organization.contact.language.english'),
              t('organization.contact.language.french'),
            ],
          },
        ]}
      />
      <RecommendationsLoader
        pageType="home"
        onLoad={(data) => setRecommendations(data)}
        trigger
      />
      <div>
        <HomepageSliceZone
          slices={firstTwoSlices}
          pageType={PageType.homePage}
          recommendations={recommendations}
        />
      </div>
      <div ref={sliceZoneRef}>
        {isSliceZoneRef ? (
          <HomepageSliceZone
            slices={belowFoldSlices}
            pageType={PageType.homePage}
            secondSliceZone
            recommendations={recommendations}
          />
        ) : (
          <Container className={styles.sliceZoneLoading}>
            <Skeleton height={800} width="100%" />
          </Container>
        )}
      </div>
    </>
  );
}

Home.Layout = Layout;
